// src/features/price/import/PriceCheckingStep.jsx

import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, Badge } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useAppContext } from "providers/AppProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUserCheck,
  faUserTimes,
  faTrash,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import { deleteRow, updateRowData } from "../config/PriceSlice";
import IconButton from "components/common/IconButton";
import EditPriceModal from "./EditPriceModal";

// Icon map for summary cards
const iconMap = {
  total: faUser,
  valid: faUserCheck,
  invalid: faUserTimes,
};

// ✅ Summary Card Component
const SummaryCard = ({ type, title, value, variant, onClick, active }) => {
  return (
    <Card
      className={`mb-3 shadow-sm border ${
        active ? `border-${variant} border-2` : "border-200"
      }`}
      style={{
        cursor: "pointer",
        transition: "all 0.3s ease",
        backgroundColor: active
          ? `var(--bs-${variant}-bg-subtle)`
          : "var(--falcon-card-bg)",
      }}
      onClick={onClick}
    >
      <Card.Body className="d-flex align-items-center justify-content-between p-3">
        <div>
          <h6 className="mb-1 text-muted">{title}</h6>
          <h2 className={`mb-0 fw-bold text-${variant}`}>{value}</h2>
        </div>
        <FontAwesomeIcon
          icon={iconMap[type]}
          className={`text-${variant} fs-2`}
        />
      </Card.Body>
    </Card>
  );
};

// ✅ Status Badge
const StatusBadge = ({ isValid }) => (
  <Badge bg={isValid ? "success" : "danger"} className="py-2 px-3 rounded-pill">
    {isValid ? "Valid" : "Invalid"}
  </Badge>
);

// ✅ Expandable Row for Error Display
const ExpandableRow = ({ data }) => (
  <div className="p-3">
    {Array.isArray(data.errors) && data.errors.length > 0 ? (
      <ul className="mb-0 ps-3 text-danger small">
        {data.errors.map((err, idx) => (
          <li key={idx}>{err}</li>
        ))}
      </ul>
    ) : (
      <p className="text-success mb-0">No errors. This row is valid.</p>
    )}
  </div>
);

// ✅ Main Component
const PriceCheckingStep = () => {
  const dispatch = useDispatch();
  const { tableData } = useSelector((state) => state.price);
  const {
    config: { theme },
  } = useAppContext();

  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectRow, setSelectRow] = useState(null);

  // Summary values
  const summaryMetrics = useMemo(() => {
    const totalRecords = tableData.length;
    const validRecords = tableData.filter((row) => row.isValid).length;
    const invalidRecords = totalRecords - validRecords;
    return { totalRecords, validRecords, invalidRecords };
  }, [tableData]);

  // Filtered table rows
  const filteredData = useMemo(() => {
    if (filterType === "valid") return tableData.filter((row) => row.isValid);
    if (filterType === "invalid")
      return tableData.filter((row) => !row.isValid);
    return tableData;
  }, [tableData, filterType]);

  // Actions
  const handleDelete = (row) => dispatch(deleteRow(row?.rowNum));
  const handleEdit = (row) => {
    setShowModal(true);
    setSelectRow(row);
  };

  // Columns for DataTable
  const columns = [
    {
      name: "#",
      selector: (row) => row.rowNum - 1,
      width: "60px",
    },
    {
      name: "Status",
      cell: (row) => <StatusBadge isValid={row.isValid} />,
      width: "100px",
    },
    {
      name: "Nphies Code",
      selector: (row) => row["Nphies Code"] || "-",
      sortable: true,
    },
    {
      name: "Provider Code",
      selector: (row) => row["Provider Service Code"] || "-",
      sortable: true,
    },
    {
      name: "Price",
      selector: (row) => row["Price"] ?? "-",
      sortable: true,
    },
    {
      name: "Discount",
      selector: (row) => row["Discount"] ?? "-",
      sortable: true,
    },
    {
      name: "VAT",
      selector: (row) => {
        const vat = row["VAT Indicator "];
        return isNaN(vat) ? "-" : vat;
      },
      sortable: true,
    },

    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <IconButton
            variant="outline-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <FontAwesomeIcon icon={faPen} />
          </IconButton>
          <IconButton
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </IconButton>
        </div>
      ),
      width: "150px",
    },
  ];

  return (
    <div className="mt-4">
      {/* ✅ Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <SummaryCard
            type="total"
            title="Total Records"
            value={summaryMetrics.totalRecords}
            variant="primary"
            onClick={() => setFilterType("all")}
            active={filterType === "all"}
          />
        </Col>
        <Col md={4}>
          <SummaryCard
            type="valid"
            title="Valid Records"
            value={summaryMetrics.validRecords}
            variant="success"
            onClick={() => setFilterType("valid")}
            active={filterType === "valid"}
          />
        </Col>
        <Col md={4}>
          <SummaryCard
            type="invalid"
            title="Invalid Records"
            value={summaryMetrics.invalidRecords}
            variant="danger"
            onClick={() => setFilterType("invalid")}
            active={filterType === "invalid"}
          />
        </Col>
      </Row>

      {/* ✅ DataTable */}
      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        highlightOnHover
        expandableRows
        expandableRowsComponent={ExpandableRow}
        // expandableRowDisabled={(row) =>
        //   !Array.isArray(row.errors) || row.errors.length === 0
        // }
        theme={theme}
        dense
      />

      <EditPriceModal
        show={showModal}
        onHide={() => setShowModal(false)}
        setSelectedRow={setSelectRow}
        selectRow={selectRow}
      />
    </div>
  );
};

export default PriceCheckingStep;
